/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */
import React, { ChangeEvent, MouseEvent, useEffect, useMemo, useState } from 'react';
import { isArray } from 'lodash';
import clsx from 'clsx';

import { Accordion } from 'carbon-react/lib/components/accordion';
import Icon from 'carbon-react/lib/components/icon';
import { MultiSelect, Option, Select } from 'carbon-react/lib/components/select';

import { useIntl } from '@futrli/localisation';

import {
  CARD_TYPES,
  DataField,
  GROUPING_OPTIONS,
  ROUNDING_OPTIONS,
  WIDGET_TYPES,
  VALUE_DISPLAY_OPTIONS
} from '../../../types';
import { useReportBuilderContext } from '../../../report-builder.context';
import {
  flattenFinFields,
  getConsolidationShortCode,
  getDefaultGrouping,
  getFieldWithChildren
} from '../../../utils-data';
import { useConsolidatedForecastingContext } from '../../../../forecast/detail/context/consolidated-forecasting-context';
import { usePredictionsInfoContext } from '../../../../forecast/detail/context/predictions-info-context';
import { useI18nConstants } from '../../../use-i18n-constants';

import styles from './settings-panel.scss';

interface Props {
  field: DataField;
  onDelete: (ref: string) => void;
  openFormulaDialog?: (id?: string) => void;
}

export const SettingsFinPanel = ({ field, onDelete, openFormulaDialog }: Props) => {
  const intl = useIntl();
  const { setFinFieldOptions, currentCard, allFinFields } = useReportBuilderContext();
  const { isConsolidation } = usePredictionsInfoContext();
  const { consolidation } = useConsolidatedForecastingContext();
  const { driversGroupingOptions, groupingOptions, roundingOptions, unitOptions } = useI18nConstants();
  const initFinFieldsOptions = currentCard && currentCard[CARD_TYPES.WIDGET]?.finFieldsOptions;
  const initFinFieldOptions = (initFinFieldsOptions || []).find(e => e.ref === field.ref);
  const chartType = currentCard && currentCard[CARD_TYPES.WIDGET]?.widgetType;
  const isCircularChartType =
    chartType === WIDGET_TYPES.PIE || chartType === WIDGET_TYPES.DONUT || chartType === WIDGET_TYPES.DATA_CARD;
  const [rounding, setRounding] = useState(initFinFieldOptions?.options?.rounding || ROUNDING_OPTIONS.INTEGER);
  const [grouping, setGrouping] = useState<GROUPING_OPTIONS[]>(
    initFinFieldOptions?.options?.grouping ||
      getDefaultGrouping(field, currentCard?.[CARD_TYPES.WIDGET]?.widgetType as WIDGET_TYPES, isConsolidation)
  );
  const [unit, setUnit] = useState<VALUE_DISPLAY_OPTIONS | undefined>(
    initFinFieldOptions?.options?.unit || VALUE_DISPLAY_OPTIONS.NUMBER
  );
  const hasComparisons = (currentCard?.[CARD_TYPES.WIDGET]?.selectedComparisons || []).length > 0;

  const isDriver =
    field.classifier === 'driver-group' ||
    field.classifier === 'freestyle-driver' ||
    field.classifier === 'formula-driver';

  const subGroupingOptions = useMemo(() => {
    let subGO = [...groupingOptions];
    if (isConsolidation || hasComparisons) {
      // if we have comparisons between datasets, we can't compare assumptions, as they are specific to each dataset
      // consolidations also don't have assumptions
      subGO = subGO.filter(e => e.value !== GROUPING_OPTIONS.ASSUMPTIONS);
    }
    switch (field.classifier) {
      case '':
      case 'static':
        return subGO;
      case 'group':
      case 'elimination-group':
      case 'group-by-account-name':
      case 'group-by-org-group-name':
        return subGO.filter(e => e.value !== GROUPING_OPTIONS.CATEGORIES);
      case 'account':
      case 'eliminated-account':
        return subGO.filter(e => e.value !== GROUPING_OPTIONS.GROUPS && e.value !== GROUPING_OPTIONS.CATEGORIES);
      case 'driver-group':
        return driversGroupingOptions;
      default:
        return [];
    }
  }, [driversGroupingOptions, field.classifier, groupingOptions, hasComparisons, isConsolidation]);

  const fwc = useMemo(() => {
    return getFieldWithChildren(allFinFields, field.ref, field.isSecondary) || field;
  }, [field, allFinFields]);

  const flattenedCF = useMemo(() => flattenFinFields([allFinFields[2]]), [allFinFields]);

  useEffect(() => {
    const initFinFieldsOptions = currentCard && currentCard[CARD_TYPES.WIDGET]?.finFieldsOptions;
    const initFinFieldOptions = (initFinFieldsOptions || []).find(e => e.ref === field.ref);
    const initialGrouping =
      initFinFieldOptions?.options?.grouping ||
      getDefaultGrouping(field, currentCard?.[CARD_TYPES.WIDGET]?.widgetType as WIDGET_TYPES, isConsolidation);

    if (isConsolidation && initialGrouping.includes(GROUPING_OPTIONS.ASSUMPTIONS)) {
      setGrouping(initialGrouping.filter(g => g !== GROUPING_OPTIONS.ASSUMPTIONS));
    } else {
      setGrouping(initialGrouping);
    }
  }, [currentCard, field, isConsolidation]);

  useEffect(() => {
    const initFinFieldsOptions = currentCard && currentCard[CARD_TYPES.WIDGET]?.finFieldsOptions;
    const initFinFieldOptions = (initFinFieldsOptions || []).find(e => e.ref === field.ref);
    const initialGrouping =
      initFinFieldOptions?.options?.grouping ||
      getDefaultGrouping(field, currentCard?.[CARD_TYPES.WIDGET]?.widgetType as WIDGET_TYPES, isConsolidation);

    if (isCircularChartType && initialGrouping.length > 1) {
      handleGroupingChange([initialGrouping[0]]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCircularChartType, field.ref, subGroupingOptions, currentCard?.i, isConsolidation]);

  const handleRoundingChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newRounding = event.target.value as ROUNDING_OPTIONS;
    if (newRounding !== rounding) {
      setRounding(newRounding);
      setFinFieldOptions(field.ref, {
        rounding: newRounding
      });
    }
  };
  const handleGroupingChange = (value: GROUPING_OPTIONS[] | GROUPING_OPTIONS) => {
    let newGrouping: GROUPING_OPTIONS[] = [];
    if (isArray(value)) {
      newGrouping = value;
    } else {
      newGrouping = [value];
    }
    if (grouping.join('#') !== newGrouping.join('#')) {
      setGrouping(newGrouping);
      setFinFieldOptions(field.ref, {
        grouping: newGrouping
      });
    }
  };
  const handleUnitChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newUnit = event.target.value as VALUE_DISPLAY_OPTIONS;
    if (newUnit !== unit) {
      setUnit(newUnit);
      setFinFieldOptions(field.ref, {
        unit: newUnit
      });
    }
  };

  const handleDelete = (event: MouseEvent<unknown>) => {
    event.stopPropagation();
    onDelete(field.ref);
  };

  const handleFormulaClick = (event: MouseEvent<unknown>) => {
    event.stopPropagation();
    openFormulaDialog?.(field.ref);
  };

  return (
    <div
      className={clsx(
        styles.expandPanelContainer,
        styles.expandPanelContainerCarbon,
        styles[field.classifier || 'parent']
      )}
    >
      <Accordion
        borders="full"
        title={
          <div className={clsx(styles.panelSummary, styles.panelSummaryCarbon)}>
            <div className={styles.leftSide}>
              <Icon type="drag" className={styles.dragIcon} />
              <h2>
                {field.ref !== 'cash_flow' && flattenedCF.find(f => f.ref === field.ref)
                  ? `${field.name} - ${intl.formatMessage({
                      id: 'financial_fields.type.cash_flow',
                      defaultMessage: 'Cash Flow'
                    })}`
                  : field.name}
                {consolidation && field.organisation_id && (
                  <span className={styles.orgCode}>
                    {getConsolidationShortCode(consolidation, field.organisation_id)}
                  </span>
                )}
              </h2>
            </div>
            <div className={clsx(styles.rightSide)} />
          </div>
        }
        headerSpacing={{ p: 0 }}
      >
        {((fwc?.children?.length || 0) > 0 || !isConsolidation) && subGroupingOptions.length > 0 && (
          <div>
            {isCircularChartType ? (
              <Select
                id="grouping"
                autoComplete="off"
                onChange={e => handleGroupingChange(e.target.value as GROUPING_OPTIONS | GROUPING_OPTIONS[])}
                value={grouping[0]}
                size="small"
                label={intl.formatMessage({ id: 'reporting.data_includes', defaultMessage: 'Data includes' })}
                mb={1}
              >
                {subGroupingOptions.map(option => (
                  <Option
                    key={option.value}
                    value={option.value}
                    text={option.name}
                    className={grouping.includes(option.value) ? styles.selectedOption : ''}
                  />
                ))}
              </Select>
            ) : (
              <MultiSelect
                id="grouping"
                autoComplete="off"
                onChange={e => handleGroupingChange(e.target.value as GROUPING_OPTIONS | GROUPING_OPTIONS[])}
                value={grouping}
                size="small"
                label={intl.formatMessage({ id: 'reporting.data_includes', defaultMessage: 'Data includes' })}
                mb={1}
                multiple={!isCircularChartType}
              >
                {subGroupingOptions.map(option => (
                  <Option
                    key={option.value}
                    value={option.value}
                    text={option.name}
                    className={grouping.includes(option.value) ? styles.selected : ''}
                  />
                ))}
              </MultiSelect>
            )}
          </div>
        )}
        {currentCard?.[CARD_TYPES.WIDGET]?.widgetType === WIDGET_TYPES.TABLE && (
          <div>
            <Select
              id="rounding"
              autoComplete="off"
              onChange={handleRoundingChange}
              value={rounding}
              size="small"
              label={intl.formatMessage({ id: 'reporting.rounding', defaultMessage: 'Rounding' })}
              mb={1}
            >
              {roundingOptions.map(option => (
                <Option key={option.value} value={option.value} text={option.name} />
              ))}
            </Select>
          </div>
        )}

        {currentCard?.[CARD_TYPES.WIDGET]?.widgetType === WIDGET_TYPES.TABLE && isDriver && (
          <div>
            <Select
              id="display"
              autoComplete="off"
              onChange={handleUnitChange}
              value={unit ?? ''}
              size="small"
              label={intl.formatMessage({ id: 'common.unit', defaultMessage: 'Unit' })}
              mb={1}
            >
              {unitOptions.map(option => (
                <Option key={option.value} value={option.value} text={option.name} />
              ))}
            </Select>
          </div>
        )}
      </Accordion>
      <div className={styles.headerButtonContainer}>
        <div className={styles.leftSide} />
        <div className={clsx(styles.rightSide)}>
          {['row-formula', 'template-formula'].includes(field.classifier) && (
            <button
              onClick={handleFormulaClick}
              aria-label={intl.formatMessage({ id: 'common.edit', defaultMessage: 'Edit' })}
            >
              {field.locked ? <Icon type="view" /> : <Icon type="edit" />}
            </button>
          )}
          <button
            onClick={handleDelete}
            aria-label={intl.formatMessage({ id: 'common.delete', defaultMessage: 'Delete' })}
          >
            <Icon type="delete" />
          </button>
        </div>
      </div>
    </div>
  );
};
